/**
 * Crisis Prediction View
 * 
 * Run crisis predictions and view results.
 */

import React, { useState } from 'react';
import { AlertTriangle, Search, Loader2, TrendingUp, Clock, Shield } from 'lucide-react';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer } from 'recharts';

import ThreatGauge from '../components/ThreatGauge';


export default function CrisisPrediction({ apiUrl }) {
  const [target, setTarget] = useState('');
  const [loading, setLoading] = useState(false);
  const [prediction, setPrediction] = useState(null);
  const [error, setError] = useState(null);

  const presetTargets = [
    'Taiwan',
    'Ukraine', 
    'South China Sea',
    'Korean Peninsula',
    'Middle East',
    'Baltic States',
    'Indo-Pacific'
  ];

  async function runPrediction() {
    if (!target) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${apiUrl}/predict/crisis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target })
      });
      
      const data = await response.json();
      if (data.error) {
        setError(data.error);
      } else {
        setPrediction(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const getThreatColor = (probability) => {
    if (probability > 0.7) return 'text-red-500';
    if (probability > 0.4) return 'text-yellow-500';
    return 'text-green-500';
  };

  const getThreatLabel = (probability) => {
    if (probability > 0.7) return 'CRITICAL';
    if (probability > 0.5) return 'HIGH';
    if (probability > 0.3) return 'ELEVATED';
    return 'LOW';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Crisis Prediction</h1>
        <p className="text-slate-400">Predict crisis probability for any region or scenario</p>
      </div>

      {/* Search Input */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <div className="flex space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder="Enter target region, country, or scenario..."
              className="w-full bg-slate-700 border border-slate-600 rounded-lg pl-10 pr-4 py-3 
                       focus:outline-none focus:ring-2 focus:ring-cyan-500"
              onKeyDown={(e) => e.key === 'Enter' && runPrediction()}
            />
          </div>
          <button
            onClick={runPrediction}
            disabled={loading || !target}
            className="px-6 py-3 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium
                     disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <AlertTriangle className="w-5 h-5 mr-2" />
                Predict
              </>
            )}
          </button>
        </div>

        {/* Preset Targets */}
        <div className="mt-4 flex flex-wrap gap-2">
          {presetTargets.map((preset) => (
            <button
              key={preset}
              onClick={() => setTarget(preset)}
              className={`px-3 py-1 rounded-full text-sm border transition-colors
                ${target === preset 
                  ? 'border-cyan-500 bg-cyan-500/20 text-cyan-400' 
                  : 'border-slate-600 hover:border-slate-500'
                }`}
            >
              {preset}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-500/20 border border-red-500 rounded-lg p-4 text-red-400">
          {error}
        </div>
      )}

      {/* Results */}
      {prediction && (
        <div className="space-y-6">
          {/* Main Result */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Probability Gauge */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h2 className="text-lg font-semibold mb-4">Crisis Probability</h2>
              <ThreatGauge value={prediction.crisisProbability} />
              <div className="mt-4 text-center">
                <div className={`text-2xl font-bold ${getThreatColor(prediction.crisisProbability)}`}>
                  {getThreatLabel(prediction.crisisProbability)}
                </div>
                <div className="text-slate-400 text-sm mt-1">
                  {(prediction.crisisProbability * 100).toFixed(1)}% probability
                </div>
              </div>
            </div>

            {/* Key Metrics */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h2 className="text-lg font-semibold mb-4">Analysis Metrics</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Clock className="w-5 h-5 text-slate-400 mr-2" />
                    <span>Time Horizon</span>
                  </div>
                  <span className="text-cyan-400 font-medium">
                    {prediction.timeHorizon || 'Unknown'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Shield className="w-5 h-5 text-slate-400 mr-2" />
                    <span>Confidence</span>
                  </div>
                  <span className="text-cyan-400 font-medium">
                    {((prediction.confidence || 0) * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <TrendingUp className="w-5 h-5 text-slate-400 mr-2" />
                    <span>Accuracy Target</span>
                  </div>
                  <span className="text-green-400 font-medium">
                    {((prediction.accuracy || 0.95) * 100).toFixed(0)}%
                  </span>
                </div>
              </div>
            </div>

            {/* Escalation Risk */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h2 className="text-lg font-semibold mb-4">Escalation Risk</h2>
              {prediction.responses?.escalationRisk ? (
                <div className="space-y-4">
                  <div className="text-center">
                    <div className={`text-3xl font-bold ${
                      prediction.responses.escalationRisk.level === 'high' ? 'text-red-500' :
                      prediction.responses.escalationRisk.level === 'medium' ? 'text-yellow-500' :
                      'text-green-500'
                    }`}>
                      {prediction.responses.escalationRisk.level?.toUpperCase()}
                    </div>
                  </div>
                  {prediction.responses.escalationRisk.factors?.length > 0 && (
                    <div>
                      <div className="text-sm text-slate-400 mb-2">Risk Factors:</div>
                      <div className="flex flex-wrap gap-1">
                        {prediction.responses.escalationRisk.factors.map((f, i) => (
                          <span key={i} className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs">
                            {f}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-slate-400 text-center py-8">
                  No escalation data available
                </div>
              )}
            </div>
          </div>

          {/* Recommendations */}
          {prediction.recommendations?.length > 0 && (
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h2 className="text-lg font-semibold mb-4">Recommendations</h2>
              <div className="space-y-3">
                {prediction.recommendations.map((rec, i) => (
                  <div 
                    key={i} 
                    className={`p-4 rounded-lg border-l-4 ${
                      rec.priority === 'critical' ? 'bg-red-500/10 border-red-500' :
                      rec.priority === 'high' ? 'bg-yellow-500/10 border-yellow-500' :
                      'bg-slate-700/50 border-slate-500'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">{rec.action}</span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        rec.priority === 'critical' ? 'bg-red-500/20 text-red-400' :
                        rec.priority === 'high' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-slate-600 text-slate-300'
                      }`}>
                        {rec.priority?.toUpperCase()}
                      </span>
                    </div>
                    {rec.rationale && (
                      <div className="text-sm text-slate-400">{rec.rationale}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Scenarios */}
          {prediction.scenarios?.length > 0 && (
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h2 className="text-lg font-semibold mb-4">Projected Scenarios</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {prediction.scenarios.slice(0, 6).map((scenario, i) => (
                  <div key={i} className="bg-slate-700/50 rounded-lg p-4">
                    <div className="font-medium mb-2">
                      {scenario.name || `Scenario ${i + 1}`}
                    </div>
                    <div className="text-sm text-slate-400">
                      {scenario.description || 'No description available'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Raw Data Toggle */}
          <details className="bg-slate-800 rounded-xl border border-slate-700">
            <summary className="p-4 cursor-pointer hover:bg-slate-700/50">
              View Raw Prediction Data
            </summary>
            <pre className="p-4 text-xs overflow-auto max-h-96 bg-slate-900">
              {JSON.stringify(prediction, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}
